import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  History, 
  UserPlus, 
  UserMinus, 
  Clock, 
  Euro, 
  Wallet,
  User,
  Building,
  Calendar,
  UserCog
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLog = () => {
  const { isSindacatoAdmin } = useAuth();
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSindacatoAdmin()) {
      toast.error('Accesso non autorizzato');
      return;
    }
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/log`);
      setLogData(res.data || {
        storico_assunzioni: [],
        ore_lavoro_log: [],
        stipendi_log: [],
        movimenti_banca: [],
        modifiche_ruolo: []
      });
    } catch (error) {
      console.error('Errore nel caricamento dei log:', error);
      // Imposta dati vuoti invece di mostrare errore
      setLogData({
        storico_assunzioni: [],
        ore_lavoro_log: [],
        stipendi_log: [],
        movimenti_banca: [],
        modifiche_ruolo: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isSindacatoAdmin()) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500">Accesso riservato agli Admin</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-log-page" className="space-y-6">
      <div>
        <h1 className="page-header flex items-center gap-3">
          <History className="w-8 h-8" />
          Log Attività
        </h1>
        <p className="text-slate-600 mt-1">
          Visualizza tutte le attività: assunzioni, licenziamenti, ore online, stipendi
        </p>
      </div>

      {/* Statistiche rapide */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-blue-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{logData?.storico_assunzioni?.length || 0}</p>
            <p className="text-sm text-blue-600">Assunzioni/Licenziamenti</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-cyan-700">{logData?.ore_lavoro_log?.length || 0}</p>
            <p className="text-sm text-cyan-600">Ore Registrate</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{logData?.stipendi_log?.length || 0}</p>
            <p className="text-sm text-green-600">Stipendi Pagati</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{logData?.movimenti_banca?.length || 0}</p>
            <p className="text-sm text-purple-600">Movimenti Banca</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{logData?.modifiche_ruolo?.length || 0}</p>
            <p className="text-sm text-amber-600">Modifiche Ruolo</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="assunzioni" className="w-full">
        <TabsList>
          <TabsTrigger value="assunzioni" className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Assunzioni/Licenziamenti
          </TabsTrigger>
          <TabsTrigger value="ore" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Ore Online
          </TabsTrigger>
          <TabsTrigger value="stipendi" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Stipendi
          </TabsTrigger>
          <TabsTrigger value="banca" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Movimenti Banca
          </TabsTrigger>
          <TabsTrigger value="ruoli" className="flex items-center gap-2">
            <UserCog className="w-4 h-4" />
            Modifiche Ruolo
          </TabsTrigger>
        </TabsList>

        {/* Tab Assunzioni/Licenziamenti */}
        <TabsContent value="assunzioni" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Storico Assunzioni e Licenziamenti</CardTitle>
            </CardHeader>
            <CardContent>
              {!logData?.storico_assunzioni?.length ? (
                <p className="text-center text-slate-500 py-8">Nessun record</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Utente</TableHead>
                        <TableHead>Azienda</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Operatore</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logData.storico_assunzioni.map((entry, idx) => (
                        <TableRow key={entry.id || idx}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={entry.tipo === 'ASSUNZIONE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {entry.tipo === 'ASSUNZIONE' ? (
                                <><UserPlus className="w-3 h-3 mr-1" />Assunto</>
                              ) : (
                                <><UserMinus className="w-3 h-3 mr-1" />Licenziato</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">@{entry.user_username}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-slate-400" />
                              {entry.azienda_nome}
                            </div>
                          </TableCell>
                          <TableCell>{entry.ruolo_nome || '-'}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            @{entry.operatore_username}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                            {entry.motivo || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ore Online */}
        <TabsContent value="ore" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Log Ore Online Registrate</CardTitle>
            </CardHeader>
            <CardContent>
              {!logData?.ore_lavoro_log?.length ? (
                <p className="text-center text-slate-500 py-8">Nessun record</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead>Ore</TableHead>
                        <TableHead>Registrato da</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logData.ore_lavoro_log.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">@{entry.user_username}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-blue-100 text-blue-700">
                              {entry.ore}h {entry.minuti}m
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {entry.registrato_da_nome}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                            {entry.note || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Stipendi */}
        <TabsContent value="stipendi" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Log Stipendi Pagati</CardTitle>
            </CardHeader>
            <CardContent>
              {!logData?.stipendi_log?.length ? (
                <p className="text-center text-slate-500 py-8">Nessun record</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead>Azienda</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Pagato da</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logData.stipendi_log.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">@{entry.user_username}</span>
                          </TableCell>
                          <TableCell>{entry.azienda_nome}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(entry.importo)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {entry.pagato_da_nome}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                            {entry.note || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Movimenti Banca */}
        <TabsContent value="banca" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Movimenti Bancari</CardTitle>
            </CardHeader>
            <CardContent>
              {!logData?.movimenti_banca?.length ? (
                <p className="text-center text-slate-500 py-8">Nessun record</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead>Causale</TableHead>
                        <TableHead className="text-right">Saldo Prec.</TableHead>
                        <TableHead className="text-right">Nuovo Saldo</TableHead>
                        <TableHead>Eseguito da</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logData.movimenti_banca.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={entry.tipo === 'ACCREDITO' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {entry.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${entry.tipo === 'ACCREDITO' ? 'text-green-600' : 'text-red-600'}`}>
                            {entry.tipo === 'ACCREDITO' ? '+' : '-'}{formatCurrency(entry.importo)}
                          </TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{entry.causale}</TableCell>
                          <TableCell className="text-right text-sm text-slate-500">
                            {formatCurrency(entry.saldo_precedente)}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {formatCurrency(entry.saldo_nuovo)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {entry.eseguito_da_nome}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Modifiche Ruolo */}
        <TabsContent value="ruoli" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCog className="w-5 h-5" />
                Modifiche Ruolo Dipendenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!logData?.modifiche_ruolo?.length ? (
                <p className="text-center text-slate-500 py-8">Nessuna modifica ruolo registrata</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Dipendente</TableHead>
                        <TableHead>Nuovo Ruolo</TableHead>
                        <TableHead>Azienda</TableHead>
                        <TableHead>Modificato da</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logData.modifiche_ruolo.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-amber-600" />
                              </div>
                              <div>
                                <p className="font-medium">@{entry.user_username}</p>
                                <p className="text-xs text-slate-500">{entry.user_nome}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-amber-100 text-amber-700">
                              {entry.ruolo_nuovo_nome || 'Nessun ruolo'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Building className="w-4 h-4 text-slate-400" />
                              <span className="text-sm">{entry.azienda_nome || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              @{entry.eseguito_da_username}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLog;
